import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt') {
        urlEncoded.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d9mtpnkit35ii3jdllsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (typeof msg === 'string' && msg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于西瓜视频
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              西瓜视频是一个致力于为广大用户提供免费高清影视资源的在线平台。
              我们汇聚了最新电影、热门电视剧、精彩综艺和优质动漫，
              让每一位用户都能在这里享受流畅、高品质的观影体验。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-12 max-w-4xl mx-auto">
            <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-movie-2-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">海量资源</h3>
              <p className="text-sm text-foreground-600">电影、电视剧、综艺、动漫全覆盖，每日更新最新内容</p>
            </div>
            <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-accent-100 flex items-center justify-center mb-4">
                <i className="ri-speed-up-line text-2xl text-accent-500"></i>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">高清流畅</h3>
              <p className="text-sm text-foreground-600">高清画质，流畅播放不卡顿，享受极致观影体验</p>
            </div>
            <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-secondary-100 flex items-center justify-center mb-4">
                <i className="ri-vip-crown-line text-2xl text-secondary-600"></i>
              </div>
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">完全免费</h3>
              <p className="text-sm text-foreground-600">无需会员，无需付费，所有内容免费在线观看</p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                  "西瓜视频真的太好用了！资源特别全，我想看的电影电视剧这里都有，而且完全不卡，画质也很清晰。最重要的是免费的，强烈推荐！"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-600">李</span>
                  </div>
                  <span className="text-xs text-foreground-500">李先生 · 影视爱好者</span>
                </div>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                  "作为追剧党，西瓜视频简直是我的福音。更新速度快，分类清晰，找剧特别方便。糖豆视频的热门内容这里也能看到，太棒了！"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-accent-600">王</span>
                  </div>
                  <span className="text-xs text-foreground-500">王女士 · 追剧达人</span>
                </div>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                  "给孩子找动画片发现的这个网站，没想到内容这么丰富。国漫、日漫都有，孩子特别喜欢看。加载速度也很快，体验非常好。"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-secondary-700">张</span>
                  </div>
                  <span className="text-xs text-foreground-500">张先生 · 家长用户</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议，欢迎通过以下表单与我们联系
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="fmq-field"
              />

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '发送留言'
                  )}
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="text-center p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-sm text-green-700">感谢您的留言！我们会尽快回复您。</p>
                </div>
              )}
              {formStatus === 'error' && (
                <div className="text-center p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="w-10 h-10 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-2">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800">邮箱</p>
                <p className="text-xs text-foreground-500">contact@xiguavideo.com</p>
              </div>
              <div>
                <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-2">
                  <i className="ri-phone-line text-lg text-accent-500"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800">电话</p>
                <p className="text-xs text-foreground-500">400-888-8888</p>
              </div>
              <div>
                <div className="w-10 h-10 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-2">
                  <i className="ri-wechat-line text-lg text-secondary-600"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800">微信</p>
                <p className="text-xs text-foreground-500">xiguavideo_official</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}